import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CommonResources } from '../../../config/CommonResources';
import { ActionType, DataType, EditionMode, ViewType } from '../../../config/Constants';
import { IConfigProps, IDisplayProps, IWhereProps } from '../../services/GraphQLShared';
import { EntityType, EntityTypeProps } from '../../services/GraphQLModel';
import BreadcrumbBar, { IBreadcrumbProps, ILinkProps, IToolbarProps } from '../BreadcrumbBar';
import { Container, Row, Col } from 'react-bootstrap';
import { ErrorPanel, GenericLoadingPanel } from './Panels';
import { Nullable } from '../../helpers/Utils';
import { ConfirmDialog, IDialogProps } from './ConfirmDialog';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useSecurity } from '../../helpers/SecurityHooks';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { Upload, UploadOnBeforeUploadEvent } from '@progress/kendo-react-upload';
import { EntityStatusType, IEntityStatusType } from '../../../config/Enums';
import { IFieldBoxProps } from '../boxes/FieldBox';
import TabContainer from '../../shared/TabContainer';
import { DisplayBox } from '../boxes/DisplayBox';
import { EditButtonRow } from '../boxes/EditButtonRow';
import { useAtom, WritableAtom } from 'jotai';
import { useState } from 'react';
import { AvatarBox } from '../boxes/StatusBox';
import React from 'react';
import { useRestByIdQuery } from '../../helpers/Hooks';
import { getter } from '@progress/kendo-react-common';
import { RESET } from 'jotai/vanilla/utils';
import { apiConfig } from '../../../config/Config';
import { RootState } from '../../../store/reducer/rootReducer';
import { changeEntityStatus, createEntity, updateEntity } from '../../../services/ApiServices';
type SetStateActionWithReset<Value> = Value | typeof RESET | ((prev: Value) => Value | typeof RESET);

export interface IFormPageProps<T extends EntityTypeProps> {
  editionMode: EditionMode;
  initialValues: T;
  callBackSubmit: (dataItem: any) => void;
  callBackCancel: () => void;
}
export interface ICreateDialogProps extends IDialogProps {
  parent?: IDisplayProps;
  width?: number;
  placement?: 'center' | 'right'; // 'right' opens the dialog as a full-height panel sliding in from the right edge
}
export interface IUpdateDialogProps extends IDialogProps {
  dataItem: any;
  width?: number;
}
export interface IParameter extends IDisplayProps {
  type: EntityType;
}
export interface IParameterAccessor {
  accessor: string;
  name: string;
}
export interface IParameterList {
  name: string;
  value: any;
}
export interface IPanelTabProps {
  parameter: IParameter;
  parameters?: IParameterList[];
  readOnly?: boolean;
}
export interface IToolbarPanelProps<T extends EntityTypeProps> {
  title: string;
  type: ActionType;
  handleClick: (id: string) => void;
  condition?: (dataItem: T) => boolean;
}
export interface IViewPanelTabProps<T extends EntityTypeProps> {
  title: string;
  component: (props: IPanelTabProps) => React.ReactElement<IPanelTabProps>;
  condition?: (dataItem: T) => boolean;
  readOnly?: (dataItem: T) => boolean;
  parameters?: IParameterAccessor[];
}
export interface ICreatePanelProps<T extends EntityTypeProps> {
  config: IConfigProps;
  filter?: IWhereProps[];
  component: (props: IFormPageProps<T>) => React.ReactElement<IFormPageProps<T>>;
  getInitialValues: (dataitem: any) => T;
  getMutation: (dataitem: T, mode: EditionMode) => any;
}
export interface IViewPanelProps<T extends EntityTypeProps> {
  config: IConfigProps;
  filter?: IWhereProps[];
  displayBoxes: IFieldBoxProps[];
  toolbar?: IToolbarPanelProps<T>[];
  tabs?: IViewPanelTabProps<T>[];
  tabSelectedAtom?: WritableAtom<number, [SetStateActionWithReset<number>], void>;
  editionModeAtom?: WritableAtom<EditionMode, [SetStateActionWithReset<EditionMode>], void>;
  component: (props: IFormPageProps<T>) => React.ReactElement<IFormPageProps<T>>;
  getInitialValues: (dataitem: any) => T;
  getMutation: (dataitem: T, mode: EditionMode) => any;
  onClickEdit?: (dataitem: any, isDefault: () => void) => void;
}
export interface ICreateDialogPanelProps<T extends EntityTypeProps> extends ICreateDialogProps, ICreatePanelProps<T> {
  parentAccessor?: string;
}
export interface IUpdateDialogPanelProps<T extends EntityTypeProps> extends IUpdateDialogProps, ICreatePanelProps<T> {}
// JSON.stringify silently drops any key whose value is undefined (unlike null), so an optional
// field left blank on the form (e.g. sourceReference) would otherwise vanish from the REST body
// entirely instead of reaching the server as an explicit empty value.
const toRestPayload = (fields: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, value === undefined ? null : value]));
export function CreatePanel<T extends EntityTypeProps>(props: ICreatePanelProps<T>): React.ReactElement {
  const navigate = useNavigate();
  const currentUserMail = useSelector((state: RootState) => state.security.userInfo?.mail);
  const [restCreateState, setRestCreateState] = useState<{ loading: boolean; error: any }>({ loading: false, error: null });
  const handleCallback = (dataItem: T) => {
    // The REST poco reuses audit fields (modifiedBy/modifiedOn) that the old GraphQL create input
    // never exposed - the server is expected to overwrite them, but they must be present to
    // satisfy [Required] validation on the bound request body.
    const payload = toRestPayload({ ...props.getMutation(dataItem, EditionMode.Create).variables, modifiedBy: currentUserMail ?? '', modifiedOn: new Date().toISOString() });
    setRestCreateState({ loading: true, error: null });
    createEntity<any>(props.config.restApiEndPoint as string, payload)
      .then((result) => navigate(`${props.config.path}/${result.id}`))
      .catch((error) => setRestCreateState({ loading: false, error }))
      .finally(() => setRestCreateState((prev) => ({ ...prev, loading: false })));
  };
  const loading = restCreateState.loading;
  const error = restCreateState.error;
  const dataBreadcrumb: IBreadcrumbProps = {
    data: {
      Type: ViewType.View,
      Toolbar: [
        {
          Type: ActionType.Cancel,
          Title: CommonResources.Button_Back,
          handleClick: () => navigate(-1),
          Visible: true,
        },
      ],
      Links: [
        { Title: props.config.area, Active: false },
        { Title: props.config.displayPlural, Path: props.config.path, Active: false },
        { Title: CommonResources.BreadCrumb_New, Active: true },
      ],
      Filters: [],
    },
  };
  const initialValues = (): T => props.getInitialValues(null);
  return (
    <>
      <BreadcrumbBar data={dataBreadcrumb.data} />
      <Container className="mt-3">
        <Row>
          <Col>
            <props.component
              editionMode={EditionMode.Create}
              initialValues={initialValues()}
              callBackSubmit={handleCallback}
              callBackCancel={() => navigate(-1)}
            />
            <GenericLoadingPanel loading={loading} />
            <ErrorPanel error={error} />
          </Col>
        </Row>
      </Container>
    </>
  );
}
interface IChangeStatusProps {
  id: Nullable<string>;
  config: IConfigProps;
  changeStatus: IEntityStatusType | undefined;
  onCompleted: () => void;
}
export function ChangeStatusDialog(props: IChangeStatusProps): React.ReactElement | null {
  const [changeStatus, setChangeStatus] = React.useState<IEntityStatusType | undefined>(props.changeStatus);
  const [messageConfirm, setMessageConfirm] = React.useState(CommonResources.Dialog_MessageDelete);
  const handleChangeStatus = (status: IEntityStatusType) => {
    // Awaited so the dialog only closes - and the caller's onCompleted refetch fires - once the
    // status change has actually persisted.
    changeEntityStatus(props.config.restApiEndPoint as string, props.id as string, status).finally(handleCancel);
  };
  const handleDelete = () => handleChangeStatus(EntityStatusType.Inactive);
  const handleActive = () => handleChangeStatus(EntityStatusType.Active);
  const handleDisable = () => handleChangeStatus(EntityStatusType.Disable);
  const handleConfirmOK = () => {
    if (changeStatus === EntityStatusType.Inactive) handleDelete();
    if (changeStatus === EntityStatusType.Active) handleActive();
    if (changeStatus === EntityStatusType.Disable) handleDisable();
  };
  const handleCancel = () => {
    setChangeStatus(undefined);
    props.onCompleted();
  };
  React.useEffect(() => {
    setChangeStatus(props.changeStatus);
    switch (props.changeStatus) {
      case EntityStatusType.Active:
        setMessageConfirm(`${CommonResources.Dialog_MessageActivate}${props.config.display.toLowerCase()}?`);
        break;
      case EntityStatusType.Disable:
        setMessageConfirm(`${CommonResources.Dialog_MessageDisable}${props.config.display.toLowerCase()}?`);
        break;
      case EntityStatusType.Disable:
        setMessageConfirm(`${CommonResources.Dialog_MessageDelete}${props.config.display.toLowerCase()}?`);
        break;
    }
  }, [props.changeStatus]);
  if (!props.id || !changeStatus) return null;
  return (
    <ConfirmDialog
      title={CommonResources.Dialog_ConfirmChangeStatusTitle}
      message={messageConfirm}
      description={CommonResources.Dialog_ConfirmClick}
      onClickOk={handleConfirmOK}
      onClickCancel={handleCancel}
      visible={true}
    />
  );
}
export function ViewPanel<T extends EntityTypeProps>(props: IViewPanelProps<T>): React.ReactElement {
  const navigate = useNavigate();
  const currentSecurity = useSecurity(props.config.type, ViewType.View);
  const currentUserMail = useSelector((state: RootState) => state.security.userInfo?.mail);
  const { id } = useParams();
  const restQuery = useRestByIdQuery(props.config.restApiEndPoint, id);
  const loading = restQuery.loading;
  const error = restQuery.error;
  const data = {
    [props.config.queryIdNode]: restQuery.data && {
      // REST pocos don't reliably share the GraphQL enum's constant-case spelling - normalize at the boundary.
      ...restQuery.data,
      status: typeof restQuery.data.status === 'string' ? restQuery.data.status.toUpperCase() : restQuery.data.status,
    },
  };
  const getName = () => (loading || error ? '' : data[props.config.queryIdNode][props.config.displayAccessor]);
  const getElement = (): T => (loading || error ? {} : data[props.config.queryIdNode]);
  const getParameter = (): IParameter =>
    loading || error
      ? { id: id, display: '', type: props.config.type }
      : {
          id: data[props.config.queryIdNode].id,
          display: data[props.config.queryIdNode][props.config.displayAccessor],
          type: props.config.type,
        };
  const [changeStatus, setChangeStatus] = React.useState<IEntityStatusType | undefined>(undefined);
  const [tabSelected, setTabSelected] = props.tabSelectedAtom ? useAtom(props.tabSelectedAtom) : useState(0);
  const handleTabSelect = (e: TabStripSelectEventArguments) => setTabSelected(e.selected);
  const [editionMode, setEditionMode] = props.editionModeAtom ? useAtom(props.editionModeAtom) : useState(EditionMode.Detail);
  const handleEditionMode = (mode: EditionMode) => {
    setEditionMode(mode);
  };
  const [restUpdateState, setRestUpdateState] = useState<{ loading: boolean; error: any }>({ loading: false, error: null });
  const updateLoading = restUpdateState.loading;
  const updateError = restUpdateState.error;
  const handleDelete = () => setChangeStatus(EntityStatusType.Inactive);
  const handleActive = () => setChangeStatus(EntityStatusType.Active);
  const handleDisable = () => setChangeStatus(EntityStatusType.Disable);
  const valueOf = (field: string) => {
    if (loading || error) return '';
    const valueGetter = getter(field);
    return loading ? '' : valueGetter(data[props.config.queryIdNode]);
  };

  const getLinks = (): ILinkProps[] => {
    if (props.config.parentPath && props.config.parentDisplayAccessor && props.config.parentIdAccessor) {
      return [
        { Title: props.config.area, Active: false },
        {
          Title: valueOf(props.config.parentDisplayAccessor),
          Path: `${props.config.parentPath}/${valueOf(props.config.parentIdAccessor)}`,
          Active: false,
        },
        { Title: props.config.displayPlural, Path: props.config.path, Active: false },
        { Title: valueOf(props.config.displayAccessor), Path: `${props.config.path}/${id}`, Active: false },
        { Title: CommonResources.BreadCrumb_View, Active: true },
      ];
    }
    return [
      { Title: props.config.area, Active: false },
      { Title: props.config.displayPlural, Path: props.config.path, Active: false },
      { Title: valueOf(props.config.displayAccessor), Path: `${props.config.path}/${id}`, Active: false },
      { Title: CommonResources.BreadCrumb_View, Active: true },
    ];
  };
  const getToolbar = (): IToolbarProps[] => {
    const result = [
      { Type: ActionType.Cancel, Title: CommonResources.Button_Back, handleClick: () => navigate(-1), Visible: true },
      {
        Type: ActionType.ShowStatus,
        Title: CommonResources.Button_ShowStatus,
        handleClick: () => null,
        Visible: loading ? false : true,
      },
    ];
    props.toolbar
      ?.filter((e) => !e.condition || e.condition(getElement()))
      .forEach((e: IToolbarPanelProps<T>) => {
        result.push({
          Type: e.type,
          Title: e.title,
          handleClick: () => e.handleClick(id ?? ''),
          Visible: loading ? false : currentSecurity.canUpdate && data[props.config.queryIdNode].status == EntityStatusType.Active,
        });
      });
    result.push({
      Type: ActionType.Disable,
      Title: CommonResources.Button_Disable,
      handleClick: handleDisable,
      Visible: loading ? false : currentSecurity.canDelete && data[props.config.queryIdNode].status == EntityStatusType.Active,
    });
    result.push({
      Type: ActionType.Undelete,
      Title: CommonResources.Button_Activate,
      handleClick: handleActive,
      Visible: loading ? false : currentSecurity.canUpdate && data[props.config.queryIdNode].status != EntityStatusType.Active,
    });
    result.push({
      Type: ActionType.Delete,
      Title: CommonResources.Button_Delete,
      handleClick: handleDelete,
      Visible: loading ? false : currentSecurity.canDelete && data[props.config.queryIdNode].status != EntityStatusType.Inactive,
    });
    return result;
  };
  const dataBreadcrumb: IBreadcrumbProps = {
    data: {
      Type: ViewType.View,
      Status: valueOf('status'),
      Toolbar: getToolbar(),
      Links: getLinks(),
      Filters: [],
    },
  };
  const handleEditDefault = () => setEditionMode(EditionMode.Update);
  const handleOnClickEdit = () => (props?.onClickEdit ? props.onClickEdit(getElement(), handleEditDefault) : handleEditDefault());
  const GetAvatarBox = () => {
    switch (props.config.type) {
      case EntityType.Market:
        return (
          <AvatarBox
            size="large"
            text={data[props.config.queryIdNode][props.config.displayAccessor]}
            isoCode={data[props.config.queryIdNode].isoCode}
          />
        );
      case EntityType.EmbedReport:
        return (
          <AvatarBox
            size="large"
            text={data[props.config.queryIdNode][props.config.displayAccessor]}
            isoCode={data[props.config.queryIdNode].market?.isoCode}
            id={data[props.config.queryIdNode].id}
          />
        );
      default:
        return <AvatarBox text={data[props.config.queryIdNode][props.config.displayAccessor]} size="large" />;
    }
  };
  const onBeforeUploadIcon = (event: UploadOnBeforeUploadEvent) => {
    event.additionalData.reportId = data[props.config.queryIdNode].id;
  };
  const DisplayPanel = () => {
    return (
      <>
        <Row>
          <Col md={2} className="d-flex justify-content-center">
            <GetAvatarBox />
          </Col>
          <Col md={10}>
            {props.displayBoxes.map((box, i) => (
              <DisplayBox
                {...box}
                key={i}
                value={valueOf(box.field)}
                icon={box.icon}
                route={box.route ? `${box.route}/${valueOf(box.field.replace('display', 'id'))}` : ''}
                type={box.type ?? DataType.String}
              />
            ))}
          </Col>
        </Row>
        <EditButtonRow visible={currentSecurity.canUpdate} onClick={handleOnClickEdit} />
        <Row style={{ marginTop: '1rem' }}>
          <Col md={2} >
          </Col>
          <Col>
            <h4>
              Select an image file if you want to replace application icon:
            </h4>
            <Upload
              defaultFiles={[]}
              batch={false}
              multiple={false}
              onBeforeUpload={onBeforeUploadIcon}
              saveUrl={`${apiConfig.resourceUri}/EmbedReport/saveicon`}
            />
          </Col>
        </Row>
      </>
    );
  };
  const handleCallback = (dataItem: any) => {
    // The REST poco reuses audit fields (modifiedBy/modifiedOn) that the old GraphQL update input
    // never exposed - the server is expected to overwrite them, but they must be present to
    // satisfy [Required] validation on the bound request body.
    const payload = toRestPayload({ ...props.getMutation(dataItem, EditionMode.Update).variables, modifiedBy: currentUserMail ?? '', modifiedOn: new Date().toISOString() });
    setRestUpdateState({ loading: true, error: null });
    updateEntity<any>(props.config.restApiEndPoint as string, payload)
      .then(() => {
        restQuery.refetch();
        handleEditionMode(EditionMode.Detail);
      })
      .catch((error) => setRestUpdateState({ loading: false, error }))
      .finally(() => setRestUpdateState((prev) => ({ ...prev, loading: false })));
  };
  const ViewForm = () => {
    if (editionMode === EditionMode.Detail)
      return (
        <Container>
          {DisplayPanel()}
        </Container>
      );
    return (
      <Container>
        <props.component
          editionMode={EditionMode.Update}
          initialValues={props.getInitialValues(data[props.config.queryIdNode])}
          callBackSubmit={handleCallback}
          callBackCancel={() => handleEditionMode(EditionMode.Detail)}
        />
        <ErrorPanel error={updateError} />
        <GenericLoadingPanel loading={updateLoading} />
      </Container>
    );
  };
  if (loading) return <GenericLoadingPanel loading={loading} />;
  if (error) return <ErrorPanel error={error} />;
  return (
    <>
      <BreadcrumbBar data={dataBreadcrumb.data} />
      <Container fluid className="mt-3 px-4">
        <Row>
          <TabContainer title={getName()}>
            <TabStrip selected={tabSelected} onSelect={handleTabSelect}>
              <TabStripTab title={CommonResources.Tab_Title}>
                {ViewForm()}
              </TabStripTab>
              {props.tabs
                ?.filter((e) => !e.condition || e.condition(getElement()))
                .map((tab: IViewPanelTabProps<T>, i: number) => (
                  <TabStripTab key={i} title={tab.title}>
                    <tab.component
                      parameter={getParameter()}
                      parameters={tab.parameters?.map((param: any) => Object.assign({ value: valueOf(param.accessor), name: param.name }))}
                      readOnly={tab.readOnly ? tab.readOnly(getElement()) : undefined}
                    />
                  </TabStripTab>
                ))}
            </TabStrip>
          </TabContainer>
        </Row>
      </Container>
      <ChangeStatusDialog
        id={id}
        config={props.config}
        changeStatus={changeStatus}
        onCompleted={() => {
          setChangeStatus(undefined);
          restQuery.refetch();
        }}
      />
    </>
  );
}

export function CreateDialogPanel<T extends EntityTypeProps>(props: ICreateDialogPanelProps<T>): React.ReactElement | null {
  if (!props.visible) return null;
  const currentUserMail = useSelector((state: RootState) => state.security.userInfo?.mail);
  const [restCreateState, setRestCreateState] = useState<{ loading: boolean; error: any }>({ loading: false, error: null });
  const handleCallback = (dataItem: any) => {
    // The REST poco reuses audit fields (modifiedBy/modifiedOn) that the old GraphQL create input
    // never exposed - the server is expected to overwrite them, but they must be present to
    // satisfy [Required] validation on the bound request body.
    const payload = toRestPayload({ ...props.getMutation(dataItem, EditionMode.Create).variables, modifiedBy: currentUserMail ?? '', modifiedOn: new Date().toISOString() });
    setRestCreateState({ loading: true, error: null });
    createEntity<any>(props.config.restApiEndPoint as string, payload)
      .then(() => props.onClickOk())
      .catch((error) => setRestCreateState({ loading: false, error }))
      .finally(() => setRestCreateState((prev) => ({ ...prev, loading: false })));
  };
  const loading = restCreateState.loading;
  const error = restCreateState.error;
  const initialValues = (): T => {
    const data = props.getInitialValues(null);
    if (props.parentAccessor) data[props.parentAccessor] = props.parent;
    return data;
  };

  const isRightPanel = props.placement === 'right';

  return (
    <Dialog
      title={`${CommonResources.Dialog_Create} ${props.config.display}`}
      width={props.width ?? 800}
      onClose={props.onClickCancel}
      className={isRightPanel ? 'dialog-right-panel' : undefined}
    >
    {/* A right panel is already full height and its content area scrolls, so only the centred dialog caps the body at 85vh.
        It clips rather than hides sideways overflow: overflow-x: hidden would make this div a scroll container and stop
        the panel's sticky button row (kendo-custom.scss) from sticking. */}
    <div className="dialog-body" style={isRightPanel ? { overflowX: 'clip' } : { maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden' }}>
      <props.component
        editionMode={EditionMode.CreateDialog}
        initialValues={initialValues()}
        callBackSubmit={handleCallback}
        callBackCancel={props.onClickCancel}
      />
      </div>
      <GenericLoadingPanel loading={loading} container="k-dialog" />
      <ErrorPanel error={error} />
    </Dialog>
  );
}

export function UpdateDialogPanel<T extends EntityTypeProps>(props: IUpdateDialogPanelProps<T>): React.ReactElement | null {
  if (!props.visible) return null;
  if (!props.dataItem) return null;
  const currentUserMail = useSelector((state: RootState) => state.security.userInfo?.mail);
  const [restUpdateState, setRestUpdateState] = useState<{ loading: boolean; error: any }>({ loading: false, error: null });
  const handleCallback = (dataItem: any) => {
    // The REST poco reuses audit fields (modifiedBy/modifiedOn) that the old GraphQL update input
    // never exposed - the server is expected to overwrite them, but they must be present to
    // satisfy [Required] validation on the bound request body.
    const payload = toRestPayload({ ...props.getMutation(dataItem, EditionMode.Update).variables, modifiedBy: currentUserMail ?? '', modifiedOn: new Date().toISOString() });
    setRestUpdateState({ loading: true, error: null });
    updateEntity<any>(props.config.restApiEndPoint as string, payload)
      .then(() => props.onClickOk())
      .catch((error) => setRestUpdateState({ loading: false, error }))
      .finally(() => setRestUpdateState((prev) => ({ ...prev, loading: false })));
  };
  const loading = restUpdateState.loading;
  const error = restUpdateState.error;
  return (
    <Dialog title={`${CommonResources.Dialog_Update} ${props.config.display}`} width={props.width ?? 800} onClose={props.onClickCancel}>
      <div className="k-panel" style={{ maxHeight: '85vh', borderWidth: '0' }}>
        <props.component
          editionMode={EditionMode.UpdateDialog}
          initialValues={props.dataItem}
          callBackSubmit={handleCallback}
          callBackCancel={props.onClickCancel}
        />
      </div>
      <GenericLoadingPanel loading={loading} container="k-dialog" />
      <ErrorPanel error={error} />
    </Dialog>
  );
}
